import { useEffect, useRef, useState } from "react";
import { useInView } from "framer-motion";

/*
 * useCountUp — counts from 0 to `value` once when the element is in view.
 * Usage:
 *   const { ref, display } = useCountUp(2500, { duration: 1800, suffix: "+" });
 *   return <div ref={ref}>{display}</div>;
 */
export default function useCountUp(value, { duration = 1500, suffix = "", prefix = "", once = true } = {}) {
  const ref = useRef(null);
  const inView = useInView(ref, { amount: 0.4, once });
  const [n, setN] = useState(0);

  useEffect(() => {
    if (!inView) return;
    let raf = 0;
    const start = performance.now();
    const tick = (t) => {
      const p = Math.min((t - start) / duration, 1);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.round(eased * value));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, value, duration]);

  const display = `${prefix}${n.toLocaleString("en-IN")}${suffix}`;
  return { ref, display };
}
